import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatSortModule, Sort } from "@angular/material/sort";
import { RouterLink } from "@angular/router";

import { FooterComponent } from "../../components/common/footer/footer";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { normalizePrefix } from "../../helpers/ip";
import { safeResource } from "../../helpers/safe-resource";
import { AuthService } from "../../services/authservice";
import { IPPrefixRowComponent } from "./components/ip-prefix-row/ip-prefix-row";
import { RequestAddPrefix } from "./components/request-add-prefix/request-add-prefix";
import { IP_PREFIXES_LIST_QUERY } from "./ip-prefixes.rgql";
import { Prefix } from "./ip-prefixes.types";

@Component({
  selector: "page-ip-prefixes",
  templateUrl: "ip-prefixes.html",
  styleUrl: "ip-prefixes.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FooterComponent,
    IPPrefixRowComponent,
    MatSortModule,
    NavigationComponent,
    TranslatePipe,
    RequestAddPrefix,
    RouterLink,
  ],
})
export class IPPrefixesPage {
  private auth = inject(AuthService);
  private apollo = inject(Apollo);

  private userLoaded = toSignal(this.auth.userLoaded, { initialValue: false });

  private prefixesResource = safeResource({
    params: () => ({ loaded: this.userLoaded(), customer: this.auth.viewingCustomer().customerId }),
    loader: async ({ params }) => {
      if (!params.loaded) return Promise.resolve([] as Prefix[]);
      return firstValueFrom(
        this.apollo.query({
          query: IP_PREFIXES_LIST_QUERY,
          variables: { customer: params.customer },
        }),
      ).then(({ data }) => data?.ipPrefixList?.page ?? []);
    },
    defaultValue: [] as Prefix[],
  });

  private prefixes = computed(() => this.prefixesResource.safeValue());

  private isPlanned = (prefix: Prefix) => prefix.ipPrefix?.state === 3;

  protected subscriptions = computed(() => this.prefixes().filter((prefix) => !this.isPlanned(prefix)));
  protected plannedSubscriptions = computed(() => this.prefixes().filter(this.isPlanned));

  protected subscriptionsLength = computed(() => this.subscriptions().length);
  protected plannedSubscriptionsLength = computed(() => this.plannedSubscriptions().length);

  private activeSort = signal<Sort>({ active: "description", direction: "asc" });

  protected sortedData = computed(() => {
    const sort = this.activeSort();
    const data = [...this.subscriptions()];
    if (!sort.active || sort.direction === "") return data;
    const isAsc = sort.direction === "asc";
    return data.sort((subA, subB) => {
      switch (sort.active) {
        case "description":
          return this.compare(subA.description, subB.description, isAsc);
        case "prefix":
          return this.compare(normalizePrefix(subA?.ipPrefix?.prefix), normalizePrefix(subB?.ipPrefix?.prefix), isAsc);
        case "startdate":
          return this.compare(subA?.startDate, subB?.startDate, isAsc);
        case "version":
          return this.compare(subA?.ipPrefix?.afi ?? "", subB?.ipPrefix?.afi ?? "", isAsc);
        case "in_use":
          return this.compare(subA?.ipSubscriptions?.length, subB?.ipSubscriptions?.length, isAsc);
        default:
          return 0;
      }
    });
  });

  protected userCanAddPrefixes = computed(
    () => this.auth.hasRole(["Infraverantwoordelijke"]) && this.auth.isSelfServiceEnabled,
  );

  protected sortData(sort: Sort) {
    this.activeSort.set(sort);
  }

  private compare<T extends number | string>(a: T, b: T, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
